import React, { useState, useEffect } from 'react';
import { Card, CardBody, Col, Nav, NavItem, NavLink, Row, TabContent, TabPane } from 'reactstrap';
import classnames from 'classnames';
import {useNavigate, useParams} from 'react-router-dom';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ResourceOverview from './OverviewTab'; // Adjust path as needed
import api, { handleApiError } from '../../../helpers/api';
import ActivitiesTab from './ActivitiesTab'; // Adjust path as needed
import placeholderImage from '../../../assets/images/placeholder.jpg';
import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";

const customStyles = `
  .resource-card {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    margin-top: -1.5rem;
  }
  
  .nav-tabs-custom .nav-link {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
    color: #2a4966;
    border-bottom: 2px solid transparent;
  }
  .nav-tabs-custom .nav-link.active {
    color: #0ab39c;
    border-bottom: 2px solid #0ab39c;
    background-color: transparent;
  }
  .avatar-title img {
    object-fit: cover;
    border-radius: 4px;
  }
`;

const Section = () => {
    const { id } = useParams();
    const [activeTab, setActiveTab] = useState('1');
    const [resource, setResource] = useState(null);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    useEffect(() => {
        const fetchResource = async () => {
            try {
                const response = await api.get(`/ressource/${id}`, {
                    withCredentials: true,
                });
                setResource(response.data.data); // Assuming response.data contains the resource
                // Increment view count (backend should handle this if needed)
            } catch (err) {
                if (err.response.data?.status === 401 || err.response.data?.status === 403) {
                    await handleApiError(err, dispatch, navigate);
                } else {
                    setError(t('create_resource.toasts.fetch_failed'));
                    toast.error(t('create_resource.toasts.fetch_failed'), {
                        toastId: `fetch-resource-error-${Date.now()}`,
                    });
                }
            }
        };
        fetchResource();
    }, [id]);

    const toggleTab = (tab) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
    };

    if (!resource) {
        return (
            <React.Fragment>
                <style>{customStyles}</style>
                <ToastContainer closeButton={false} limit={1} />
                {error && <div className="alert alert-danger">{error}</div>}
                <p>Loading...</p>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <style>{customStyles}</style>
            <ToastContainer closeButton={false} limit={1} />
            {error && <div className="alert alert-danger">{error}</div>}
            <Row style={{ padding: '10px 0px ' }}>
                <Col lg={12}>
                    <Card className="mt-n4 mx-n4 resource-card">
                        <div className="bg-soft-warning">
                            <CardBody className="pb-0 px-4">
                                <Row className="mb-3">
                                    <div className="col-md">
                                        <Row className="align-items-center g-3">
                                            <div className="col-md-auto">
                                                <div className="avatar-md">
                                                    <div className="avatar-title bg-white rounded-circle">
                                                        <img
                                                            src={resource.openGraphImage || placeholderImage}
                                                            alt={resource.titre}
                                                            className="avatar-xs"
                                                            onError={(e) => { e.target.src = placeholderImage; }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md">
                                                <div>
                                                    <h4 className="fw-bold">{resource.titre}</h4>
                                                    <div className="hstack gap-3 flex-wrap">
                                                        <div>Type: <span className="fw-medium">{resource.type}</span></div>
                                                        <div className="vr"></div>
                                                        <div>Category: <span className="fw-medium">{resource.categorie || 'Uncategorized'}</span></div>
                                                        <div className="vr"></div>
                                                        <div>Views: <span className="fw-medium">{resource.vues.toLocaleString()}</span></div>
                                                        <div className="vr"></div>
                                                        <div>Publication Date: <span className="fw-medium">
                                                            {new Date(resource.publicationDate).toLocaleDateString('en-GB', {
                                                                day: '2-digit',
                                                                month: 'short',
                                                                year: 'numeric',
                                                            })}
                                                        </span></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Row>
                                    </div>
                                </Row>

                                <Nav className="nav-tabs-custom border-bottom-0" role="tablist">
                                    <NavItem>
                                        <NavLink
                                            className={classnames({ active: activeTab === '1' }, 'fw-semibold')}
                                            onClick={() => { toggleTab('1'); }}
                                            href="#"
                                        >
                                            Overview
                                        </NavLink>
                                    </NavItem>

                                </Nav>
                            </CardBody>
                        </div>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col lg={12}>
                    <TabContent activeTab={activeTab} className="text-muted">
                        <TabPane tabId="1">
                            <ResourceOverview resource={resource} />
                        </TabPane>
                        <TabPane tabId="2">
                            <ActivitiesTab />
                        </TabPane>
                    </TabContent>
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default Section;